import { useMutation, useQueryClient } from '@tanstack/react-query'
import { CHILDREN_LISTS, userQuery } from '../../account/queries'
import { type User, type UserUpdate, updateUser } from '../../api/users'
import { currentAuth, session } from '../../state/session'

/**
 * Save changes to a user and keep every view of that user in step.
 *
 * The record the API returns is written straight into the cache — the user's
 * own query, and the household's children list it may also appear in — so
 * nothing refetches after a save. When the user is the one signed in, the
 * session store is updated too, which is what makes the nav button pick up a
 * renamed parent with no coordination.
 */
export function useUpdateUser(user: User) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (update: UserUpdate) => updateUser(user.id, update, { auth: currentAuth() }),
    onSuccess: (updated) => {
      queryClient.setQueryData(userQuery(updated.id).queryKey, updated)
      queryClient.setQueriesData<User[]>(CHILDREN_LISTS, (children) =>
        children?.map((child) => (child.id === updated.id ? updated : child)),
      )
      if (session.get().userId === updated.id) session.get().setUser(updated)
    },
  })
}
